import { useState } from 'react';
import { motion } from 'motion/react';
import { BookOpen, Search, Clock, ArrowRight } from 'lucide-react';
import { geminiService } from '../services/geminiService';
import ContentModal from '../components/ContentModal';

const articles = [
  {
    title: "The Stoic Feminine",
    category: "Communication",
    date: "Apr 12, 2026",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800",
    desc: "Commanding respect in high-stakes dynamics through strategic silence and emotional discipline."
  },
  {
    title: "Vetting for Character vs. Capital",
    category: "Psychology",
    date: "Mar 28, 2026",
    image: "https://images.unsplash.com/photo-1490127252417-7c393f993ee4?auto=format&fit=crop&q=80&w=800",
    desc: "The high-earning woman's dilemma: distinguishing between financial parity and structural reliability."
  },
  {
    title: "Emotional Labor Audits",
    category: "Boundaries",
    date: "Mar 15, 2026",
    image: "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?auto=format&fit=crop&q=80&w=800",
    desc: "A clinical guide to divesting from the management of a partner's insecurities or logistical failures."
  }
];

export default function BlogLibrary() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState({ title: '', category: '', content: '' });
  const [loading, setLoading] = useState(false);

  const handleOpenArticle = async (title: string, category: string) => {
    setModalOpen(true);
    setLoading(true);
    setSelectedContent({ title, category, content: '' });
    
    try {
      const content = await geminiService.generateBriefing(title, category);
      setSelectedContent(prev => ({ ...prev, content }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-theme-bg">
      <ContentModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedContent.title}
        category={selectedContent.category}
        content={selectedContent.content}
        isLoading={loading}
      />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div>
            <span className="kicker">Video Essays & Private Briefings</span>
            <h1>The Archive</h1>
            <p className="text-taupe text-sm italic">Conceptual frameworks and psychological leverage for high-achieving women.</p>
          </div>
          <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-taupe" />
                <input
                    type="text"
                    placeholder="Search Archive..."
                    className="bg-transparent border border-theme-border px-10 py-2 text-xs uppercase tracking-widest text-theme-text focus:outline-none focus:border-gold/50 transition-all w-full md:w-64"
                />
          </div>
        </div>

        {/* Featured Post */}
        <section 
          className="group cursor-pointer mb-24 relative overflow-hidden border border-theme-border bg-theme-bg"
          onClick={() => handleOpenArticle("The ROI of the Walk-Away", "Masterclass")}
        >
            <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="aspect-video md:aspect-auto overflow-hidden">
                    <img
                        src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1200"
                        alt="Featured"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                        referrerPolicy="no-referrer"
                    />
                </div>
                <div className="p-12 flex flex-col justify-center">
                    <span className="text-[10px] uppercase tracking-[0.5em] text-gold mb-4 block">Pinned Masterclass</span>
                    <h2 className="text-3xl md:text-4xl font-serif italic mb-6 leading-tight">The ROI of the Walk-Away: Why Leverage Belongs to the Unattached</h2>
                    <p className="text-taupe mb-8 leading-relaxed italic">Strategic detachment is the highest form of negotiation. A clinical guide to generating power through the willingness to leave, rather than the desire to fix.</p>
                    <div className="flex items-center text-[10px] uppercase tracking-widest text-gold font-bold">
                        Watch Full Essay <ArrowRight className="ml-2 h-3 w-3" />
                    </div>
                </div>
            </div>
        </section>

        {/* Article Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {articles.map((article, i) => (
                <motion.article
                    key={i}
                    whileHover={{ y: -10 }}
                    className="flex flex-col group cursor-pointer"
                    onClick={() => handleOpenArticle(article.title, article.category)}
                >
                    <div className="relative aspect-square overflow-hidden mb-6 border border-theme-border">
                        <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                            referrerPolicy="no-referrer"
                        />
                        <div className="absolute top-4 left-4 bg-theme-bg/90 px-3 py-1 border border-theme-border">
                            <span className="text-[9px] uppercase tracking-widest text-gold">{article.category}</span>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4 mb-4 text-[10px] uppercase tracking-widest text-taupe">
                        <Clock className="h-3 w-3" />
                        <span>{article.date}</span>
                    </div>
                    <h3 className="text-xl font-serif italic mb-4 group-hover:text-gold transition-colors">{article.title}</h3>
                    <p className="text-sm text-taupe leading-relaxed mb-6 italic">{article.desc}</p>
                    <div className="mt-auto flex items-center text-[10px] uppercase tracking-widest text-gold opacity-0 group-hover:opacity-100 transition-opacity">
                        View Briefing <ArrowRight className="ml-2 h-3 w-3" />
                    </div>
                </motion.article>
            ))}
        </div>

        <div className="mt-24 text-center">
             <button className="btn-outline">Load More Archives</button>
        </div>
      </div>
    </div>
  );
}
